import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '../../components/Skeleton';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { SettingsNav } from '../../components/SettingsNav';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { relativeTime, shortDate } from '../../lib/format';

export function DevicesPage() {
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: () => api.auth.sessions.list(),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.auth.sessions.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.sessions }),
  });
  return (
    <AppShell title="Devices">
      <SettingsNav />
      <p className="mb-3 text-sm text-muted">
        Everywhere your account is signed in. Revoke anything you don’t recognise.
      </p>
      {sessions.isPending ? <Skeleton lines={3} /> : null}
      {sessions.isError ? <Alert tone="error">{messageForError(sessions.error)}</Alert> : null}
      {revoke.isError ? <Alert tone="error">{messageForError(revoke.error)}</Alert> : null}
      <ul className="flex flex-col gap-2">
        {sessions.data?.sessions.map((s) => (
          <li
            key={s.id}
            className={`flex items-center justify-between gap-3 rounded-xl border bg-surface p-4 ${s.current ? 'border-brand' : 'border-line'}`}
          >
            <div className="min-w-0">
              <p className="truncate font-medium">
                {s.deviceName ?? (s.kind === 'refresh' ? 'Mobile app' : 'Web browser')}
                {s.current ? (
                  <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-xs text-brand">
                    This device
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-muted">
                Active {relativeTime(s.lastSeenAt)} · since {shortDate(s.createdAt)} · expires{' '}
                {shortDate(s.expiresAt)}
              </p>
            </div>
            {!s.current ? (
              <button
                type="button"
                onClick={() => revoke.mutate(s.id)}
                disabled={revoke.isPending}
                className="min-h-9 rounded-lg border border-line px-3 text-sm text-fail hover:bg-red-50"
              >
                Revoke
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </AppShell>
  );
}
